/**
 * How the fleet table divides the width it is given.
 *
 * Pure, so the arithmetic can be tested without a browser. `VehicleTable`
 * measures its scroll box, describes each candidate column, and asks this for
 * which columns to show and how wide — then hands those widths to TanStack, so
 * the resize handles and the rendered <col>s always agree.
 *
 * Three rules, in order:
 *
 *   1. **Core columns are always shown.** Optional ones are promoted in
 *      priority order while they fit at their minimum width, and the walk
 *      stops at the first that doesn't — a low-priority narrow column must
 *      not appear while a higher-priority wide one is missing.
 *   2. **A column is never narrower than its minimum** — the width of its
 *      widest value or header, measured by the caller. When the minimums alone
 *      exceed the box the table scrolls sideways; that floor is unchanged.
 *   3. **Leftover width is shared, not parked.** Columns with `grow > 0` split
 *      the slack in proportion to their weight, so the row fills the box at
 *      any resolution instead of a spacer doing it. A column the reader has
 *      pinned (dragged or nudged) keeps exactly that width and takes no slack.
 *
 * Unmeasured (`available === null`: jsdom, or before the first observation)
 * renders the default set at minimum widths — which is precisely the layout
 * the table had before it learned to fill.
 */
export interface ColumnSpec {
  id: string;
  /** Narrowest the column may be — its widest value or header, measured. */
  min: number;
  /** Share of any leftover width. 0 never grows. */
  grow: number;
  /** Always shown, whatever the width. */
  core?: boolean;
  /** Lower comes first when the width allows more columns. Optional only. */
  priority?: number;
  /** Shown when the box has not been measured. Optional only. */
  defaultVisible?: boolean;
}

export interface ColumnLayout {
  /** Visible column ids, in display order. */
  visible: string[];
  /** Rendered width of each visible column. */
  widths: Record<string, number>;
  /** Their sum — the table's min-width, so it scrolls when that exceeds the box. */
  total: number;
}

export const MIN_COLUMN = 80;
export const MAX_COLUMN = 900;

const clamp = (n: number) =>
  Math.min(MAX_COLUMN, Math.max(MIN_COLUMN, Math.round(n)));

export function planColumns(
  specs: readonly ColumnSpec[],
  available: number | null,
  pinned: Readonly<Record<string, number>> = {},
): ColumnLayout {
  const base = (s: ColumnSpec) =>
    s.id in pinned ? clamp(pinned[s.id]) : Math.round(s.min);

  const core = specs.filter((s) => s.core);
  const optional = specs.filter((s) => !s.core);

  const chosen = new Set(core.map((s) => s.id));
  if (available === null) {
    optional.filter((s) => s.defaultVisible).forEach((s) => chosen.add(s.id));
  } else {
    let used = core.reduce((n, s) => n + base(s), 0);
    const byPriority = [...optional].sort(
      (a, b) => (a.priority ?? 99) - (b.priority ?? 99),
    );
    for (const s of byPriority) {
      if (used + base(s) > available) break;
      chosen.add(s.id);
      used += base(s);
    }
  }

  const visible = specs.filter((s) => chosen.has(s.id));
  const widths: Record<string, number> = {};
  let total = 0;
  for (const s of visible) {
    widths[s.id] = base(s);
    total += widths[s.id];
  }

  if (available !== null && total < available) {
    const growers = visible.filter((s) => !(s.id in pinned) && s.grow > 0);
    const weight = growers.reduce((n, s) => n + s.grow, 0);
    if (weight > 0) {
      const slack = available - total;
      let given = 0;
      growers.forEach((s, i) => {
        // The last grower takes the rounding remainder, so the columns sum to
        // the box exactly and no 1px spacer reappears.
        const share =
          i === growers.length - 1
            ? slack - given
            : Math.round((slack * s.grow) / weight);
        widths[s.id] = Math.min(MAX_COLUMN, widths[s.id] + share);
        given += share;
      });
      total = visible.reduce((n, s) => n + widths[s.id], 0);
    }
  }

  return { visible: visible.map((s) => s.id), widths, total };
}
